/**
 * Runtime sprite-URL setup.
 *
 * Why this exists. The SDK ships seven PNGs in `dist/duck/` and
 * `tokens.css` declares `--dddk-avatar-url: url('./duck/neutral.png')`
 * etc. In a Vite / Webpack app that imports the SDK's `styles.css`,
 * those relative URLs get resolved + hashed at build time and
 * generally work. **But** three SDK subsystems (Dwell, palette
 * cursor, palette footer brand mark) inject their own `<style>`
 * blocks at runtime that reference the same `--dddk-*-url` variables
 * via `background-image: var(...)`. Chrome resolves `url()` inside
 * runtime-injected styles against the DOCUMENT URL — not the source
 * CSS file — so the SDK's relative `./duck/…` breaks on subdirectory
 * hosts and on any setup that isn't standard Vite production.
 *
 * The fix. At `DotDotDuck` construction time, compute the sprite URLs
 * from `new URL('./duck/x.png', import.meta.url)`. Bundlers (Vite,
 * Webpack, esbuild) recognise this pattern and process the referenced
 * PNGs as assets — copying to the bundler's output dir + rewriting
 * the string at build time — so hosts using standard bundlers get
 * URLs that always resolve without any explicit configuration. For
 * non-bundler setups (esm.sh, raw `<script type="module">`) the URLs
 * resolve against the SDK JS's actual location, which is where the
 * PNGs also live.
 *
 * We set the resolved URLs on `document.documentElement.style` (inline
 * style, wins over CSS declarations without `!important`). Hosts
 * wanting to override for their own brand still use `autoInstall({
 * sprites: { avatar: '/my-brand/mascot.png' } })` — that override
 * beats the runtime default because we consult it before setting.
 */

export interface SpriteOverrides {
  /** Subtitle bar / Dwell chip / FAB fallback (`--dddk-avatar-url`). */
  avatar?: string;
  /** Proactive swim indicator, subtitle swim relay (`--dddk-swim-url`). */
  swim?: string;
  /** HERO capsule big-reveal sprite (`--dddk-hero-url`). */
  hero?: string;
  /** Dwell frame corner mascot (`--dddk-chill-url`). */
  chill?: string;
  /** Dwell top-edge 8-frame spritesheet (`--dddk-swim-cycle-url`). */
  swimCycle?: string;
  /** Palette "Powered by dotdotduck" brand mark (`--dddk-brand-mark-url`). */
  brandMark?: string;
  /** WebAgent synthetic cursor pointer mode (`--dddk-cursor-url`). */
  cursor?: string;
}

/** Vite/Webpack/esbuild recognise `new URL(literal, import.meta.url)`
 *  and process the referenced asset at build time. Keep each call as
 *  a plain literal — no template strings, no variables — so static
 *  analysis catches them. */
function bundledSpriteUrls(): Record<keyof SpriteOverrides, string> {
  return {
    avatar:    new URL('./duck/neutral.png',      import.meta.url).href,
    swim:      new URL('./duck/swim-side.png',    import.meta.url).href,
    hero:      new URL('./duck/hero-greet.png',   import.meta.url).href,
    chill:     new URL('./duck/chill-shades.png', import.meta.url).href,
    swimCycle: new URL('./duck/swim-cycle.png',   import.meta.url).href,
    brandMark: new URL('./duck/logo.png',         import.meta.url).href,
    cursor:    new URL('./duck/cursor.png',       import.meta.url).href,
  };
}

const CSS_VAR_BY_KEY: Record<keyof SpriteOverrides, string> = {
  avatar:    '--dddk-avatar-url',
  swim:      '--dddk-swim-url',
  hero:      '--dddk-hero-url',
  chill:     '--dddk-chill-url',
  swimCycle: '--dddk-swim-cycle-url',
  brandMark: '--dddk-brand-mark-url',
  cursor:    '--dddk-cursor-url',
};

/**
 * Set sprite URLs on `:root.style` inline. Called once at DotDotDuck
 * construction — cheap enough to redo (idempotent). Overrides win
 * per-key; unspecified keys fall back to the SDK-bundled sprite.
 */
export function initSpriteDefaults(overrides: SpriteOverrides = {}): void {
  if (typeof document === 'undefined') return;
  const bundled = bundledSpriteUrls();
  const root = document.documentElement;
  for (const key of Object.keys(CSS_VAR_BY_KEY) as (keyof SpriteOverrides)[]) {
    const varName = CSS_VAR_BY_KEY[key];
    const url = overrides[key] ?? bundled[key];
    root.style.setProperty(varName, `url(${url})`);
  }
}
